import DOMPurify from "dompurify";
import { PSI_PLATFORM_BE_URL } from "@/hooks/api/config";

/**
 * Contenido con formato del newsletter y las novedades de Comunidad (editor visual). Se guarda
 * como HTML en el mismo campo `body` que antes era texto plano: lo viejo se sigue mostrando
 * como siempre (`isRichHtml` = false) y al abrirlo en el editor se convierte solo.
 */

/** Colores fijos de la marca para el texto — oscuros a propósito, para leerse sobre blanco. */
export const BRAND_TEXT_COLORS = [
  { name: "Violeta oscuro", hex: "#2D2049" },
  { name: "Violeta", hex: "#665090" },
  { name: "Lavanda", hex: "#8475B3" },
  { name: "Rosa", hex: "#D4537E" },
  { name: "Verde", hex: "#5A7A12" },
  { name: "Naranja", hex: "#C46A1A" },
] as const;

const ALLOWED_COLORS = new Set<string>(BRAND_TEXT_COLORS.map((c) => c.hex.toLowerCase()));

/** Tamaños para agrandar una frase del texto común (en `em`: relativos al texto de alrededor). */
export const TEXT_SIZES = [
  { name: "Texto grande", label: "A+", value: "1.2em" },
  { name: "Texto muy grande", label: "A++", value: "1.45em" },
] as const;

const ALLOWED_SIZES = new Set<string>(TEXT_SIZES.map((s) => s.value));

/** Imágenes subidas desde el panel: se guardan con esta ruta y se sirven desde el backend. */
const MEDIA_PATH = "/api/comunidad/media/";

export const isRichHtml = (body: string | null | undefined) => !!body && body.trimStart().startsWith("<");

/**
 * Bloque "Voces de la Red" que se ubica desde el editor del newsletter: marca DÓNDE va el
 * renglón para que la psicóloga responda la consigna. En la pantalla se reemplaza por la
 * caja de respuesta (ver `splitAtVoces` y `RichContent.renderVoces`).
 */
export const VOCES_ATTR = "data-voces-de-la-red";

export const hasVocesBlock = (html: string | null | undefined) => !!html && html.includes(VOCES_ATTR);

/**
 * Parte el HTML (ya sanitizado) en lo que va antes y después del bloque de Voces de la Red.
 * Solo cuenta el primero que esté a nivel raíz; cualquier otro se descarta. `null` si no hay.
 *
 * Seguridad: cada mitad se serializa con el `innerHTML` de un contenedor (que vuelve a
 * escapar el texto — armarlo a mano con `textContent` des-escapaba `&lt;…&gt;` y reabría
 * la puerta a HTML ejecutable) y además se vuelve a sanitizar.
 */
export function splitAtVoces(safeHtml: string): { before: string; after: string } | null {
  if (!hasVocesBlock(safeHtml)) return null;
  const doc = new DOMParser().parseFromString(safeHtml, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  const idx = nodes.findIndex((n) => n instanceof Element && n.hasAttribute(VOCES_ATTR));
  if (idx === -1) return null;
  const html = (list: ChildNode[]) => {
    const box = doc.createElement("div");
    list
      .filter((n) => !(n instanceof Element && n.hasAttribute(VOCES_ATTR)))
      .forEach((n) => box.appendChild(n));
    return sanitizeRichHtml(stripVoces(box.innerHTML));
  };
  return { before: html(nodes.slice(0, idx)), after: html(nodes.slice(idx + 1)) };
}

/** Saca los bloques de Voces de la Red (para contenido donde no corresponde mostrarlos). */
export function stripVoces(safeHtml: string): string {
  if (!hasVocesBlock(safeHtml)) return safeHtml;
  const doc = new DOMParser().parseFromString(safeHtml, "text/html");
  doc.querySelectorAll(`[${VOCES_ATTR}]`).forEach((el) => el.remove());
  return doc.body.innerHTML;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Texto plano de antes → HTML para abrirlo en el editor. Respeta párrafos (línea en blanco),
 * saltos de línea, `**negrita**` y las imágenes `![alt](url)` que ya usaban las novedades.
 */
export function plainTextToHtml(text: string): string {
  if (!text.trim()) return "";
  return text
    .split(/\n{2,}/)
    .map((paragraph) => {
      const img = paragraph.trim().match(/^!\[([^\]]*)\]\((\S+?)\)$/);
      if (img) return `<img src="${escapeHtml(img[2])}" alt="${escapeHtml(img[1])}">`;
      const inline = escapeHtml(paragraph)
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
      return `<p>${inline}</p>`;
    })
    .join("");
}

/** HTML → texto plano, para adelantos ("Leer entero →") y para validar que no esté vacío. */
export function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(`[${VOCES_ATTR}]`).forEach((el) => el.remove());
  doc.querySelectorAll("p, h1, h2, li, br").forEach((el) => el.append("\n"));
  return (doc.body.textContent ?? "").replace(/\n{2,}/g, "\n").trim();
}

let hooksInstalled = false;
function installHooks() {
  if (hooksInstalled) return;
  hooksInstalled = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    const el = node as Element;
    // Dentro de `style` solo pasan los colores de la paleta y los tamaños de TEXT_SIZES.
    if (el.hasAttribute("style")) {
      const color = (el as HTMLElement).style?.color;
      const size = (el as HTMLElement).style?.fontSize;
      const hex = color ? rgbToHex(color) : null;
      el.removeAttribute("style");
      const kept = [
        hex && ALLOWED_COLORS.has(hex) ? `color: ${hex}` : null,
        size && ALLOWED_SIZES.has(size) ? `font-size: ${size}` : null,
      ].filter(Boolean);
      if (kept.length) el.setAttribute("style", kept.join("; "));
    }
    if (el.tagName === "A") {
      const href = el.getAttribute("href") ?? "";
      if (!/^https?:\/\//i.test(href) && !href.startsWith("/")) el.removeAttribute("href");
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
    if (el.tagName === "IMG") {
      const src = el.getAttribute("src") ?? "";
      if (src.startsWith(MEDIA_PATH)) {
        el.setAttribute("src", `${PSI_PLATFORM_BE_URL}${src}`);
      } else if (!/^https:\/\//i.test(src) && !src.startsWith("/comunidad-media/")) {
        el.remove();
        return;
      }
      el.setAttribute("loading", "lazy");
      const variant = el.getAttribute("data-variant");
      if (variant && !["banner", "inline", "sticker"].includes(variant)) el.removeAttribute("data-variant");
    }
  });
}

/** "rgb(45, 32, 73)" o "#2d2049" → "#2d2049". */
function rgbToHex(color: string): string | null {
  const c = color.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(c)) return c;
  const m = c.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (!m) return null;
  return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("")}`;
}

/** HTML del editor → HTML seguro para mostrar (solo las etiquetas que el editor produce). */
export function sanitizeRichHtml(html: string): string {
  installHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "h1", "h2", "strong", "b", "em", "i", "u", "s", "span", "ul", "ol", "li", "a", "img", "blockquote", "div"],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "style", "data-variant", "loading", VOCES_ATTR],
  });
}
